import { AfterViewInit, Component, Inject, OnInit, ViewChild } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatSelect } from '@angular/material/select';
import { Store } from '@ngrx/store';
import { take } from 'rxjs/operators';
import { AppState } from '../../../../../core/reducers';
import { QueryParamsModel } from '../../../../../core/_base/crud';
import { Category } from '../../../documents-management/models';
import { DocumentService } from '../../../documents-management/services';
import { Template, Form, FieldTemplateMap, FieldTemplateMapProps } from '../../models';
import { FormsTemplateService, FormService } from '../../services';
import { CreateFormTemplateMapOnServer, UpdateFormTemplateMapOnServer } from '../../actions';
import { CreateFormSchemaComponent } from '../create-form-schema/create-form-schema.component';
import { GeneralAutocompleteComponent } from '../../../../partials/layout/general-autocomplete/general-autocomplete.component';

@Component({
  selector: 'kt-create-form-template-map',
  templateUrl: './create-form-template-map.component.html',
  styleUrls: ['./create-form-template-map.component.scss'],
})
export class CreateFormTemplateMapComponent implements AfterViewInit, OnInit {
  mapping: FieldTemplateMapProps[];
  mapIndex = '';
  formFields = [];
  templateKeys = [];
  linked = false;
  mapped = true;
  getFormAutocomplete = this.formService.findForms.bind(this.formService, {});
  getTemplateAutocomplete = this.templateService.findTemplates.bind(this.templateService);
  getCategoryAutocomplete = this.documentService.findCategories.bind(this.documentService, {});
  @ViewChild('formField') formField: MatSelect;
  @ViewChild('templateKey') templateKey: MatSelect;
  @ViewChild('formAutocomplete') formAutocomplete: GeneralAutocompleteComponent;
  @ViewChild('templateAutocomplete') templateAutocomplete: GeneralAutocompleteComponent;
  @ViewChild('categoryAutocomplete') categoryAutocomplete: GeneralAutocompleteComponent;

  constructor(
    @Inject(MAT_DIALOG_DATA) public link: FieldTemplateMap,
    private dialogRef: MatDialogRef<CreateFormTemplateMapComponent>,
    private dialog: MatDialog,
    private store: Store<AppState>,
    private formService: FormService,
    private templateService: FormsTemplateService,
    private documentService: DocumentService,
  ) {}

  ngOnInit(): void {
    this.mapping = this.link.mapping ? JSON.parse(this.link.mapping) : [];

    if (this.link.model?.id)
      this.formService
        .getFormSynonymLinks(this.link.model as Form)
        .pipe(take(1))
        .subscribe(({ data: { links } }) => {
          if (!links?.length) return;

          this.formFields = links.map((link) => link.field_name);
        });
  }

  ngAfterViewInit(): void {
    if (this.link.id || this.link.require_reset) {
      this.formAutocomplete?.setValue(this.link.model);
      this.formAutocomplete?.updateValueAndValidity();
      this.templateAutocomplete?.setValue(this.link.template);
      this.templateAutocomplete?.updateValueAndValidity();

      if (this.link.document_category_id)
        this.documentService
          .getCategoryById(this.link.document_category_id)
          .pipe(take(1))
          .subscribe((res) => {
            this.categoryAutocomplete?.setValue(res?.data);
            this.categoryAutocomplete?.updateValueAndValidity();
          });

      const destinations = this.mapping.map((obj) => obj.destination);

      setTimeout(() => {
        this.templateKeys = this.link.template?.keys
          ? JSON.parse(this.link.template.keys)
              .filter((k) => !destinations.includes(k.key?.trim()))
              .map((k) => k.key)
          : [];
      });
    }
  }

  getTitle(): string {
    return `FORMS_TEMPLATES_MODULE.MAPPING.${
      this.link.id ? 'UPDATE_TITLE' : this.link.require_reset ? 'RESET_TITLE' : 'CREATE_TITLE'
    }`;
  }

  checkLink(): void {
    if (
      this.link.id ||
      this.link.require_reset ||
      !this.formAutocomplete.value?.id ||
      !this.templateAutocomplete.value?.id
    )
      return;

    const queryParams = new QueryParamsModel(
      { formId: this.formAutocomplete.value.id, templateId: this.templateAutocomplete.value.id },
      'asc',
      'modelName',
      0,
      25,
    );

    this.formService
      .getFormTemplateMapPage(queryParams)
      .pipe(take(1))
      .subscribe((res: any) => {
        this.linked = !!res.data?.length;
      });
  }

  formatFormOption(form: Form): string {
    return form?.name || '';
  }

  handleFormChange(form: Form): void {
    if (form?.id)
      this.formService
        .getFormSynonymLinks(form)
        .pipe(take(1))
        .subscribe(({ data: { links } }) => {
          if (!links?.length) return;

          this.formFields = links.map((link) => link.field_name);
          this.checkLink();
        });
  }

  formatTemplateOption(template: Template): string {
    return template?.name?.replace(/\.[^/.]+$/, '') || '';
  }

  handleTemplateChange(template: Template): void {
    this.templateKeys = template?.keys ? JSON.parse(template.keys).map((k) => k.key?.trim()) : [];
    this.checkLink();
  }

  formatCategoryOption(category: Category): string {
    return category?.name || '';
  }

  viewFormSchema(event: Event): void {
    event.stopPropagation();

    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.closeOnNavigation = true;
    dialogConfig.width = '60vw';
    dialogConfig.data = { form: this.link.model, viewMode: true };

    this.dialog.open(CreateFormSchemaComponent, dialogConfig);
  }

  downloadTemplateFile(event: Event): void {
    event.stopPropagation();

    window.open(this.link.template?.url, '_blank').focus();
  }

  copyPrevMapping(event: Event, btn: HTMLButtonElement): void {
    event.stopPropagation();

    btn.disabled = true;

    JSON.parse(this.link.prev_mapping || '[]').forEach((map) => {
      if (this.formFields.includes(map.source)) {
        this.mapping.push(map);
        this.templateKeys = this.templateKeys.filter((key) => key !== map.destination);
      }
    });
  }

  addEditMap(event: Event): void {
    event.stopPropagation();

    const map: FieldTemplateMapProps = {
      source: this.formField.value,
      destination: this.templateKey.value,
    };

    this.mapping.push(map);

    this.templateKeys = this.templateKeys.filter((key) => key !== this.templateKey.value);
    this.mapIndex = '';
    this.formField.value = '';
    this.templateKey.value = '';
  }

  prepareEditMap(event: Event, index: number): void {
    event.stopPropagation();

    this.mapIndex = index.toString();

    const map = this.mapping[index];

    this.mapping.splice(index, 1);
    this.templateKeys.push(map.destination);
    this.formField.value = map.source;
    this.templateKey.value = map.destination;
  }

  deleteMap(event: Event, index: number): void {
    event.stopPropagation();

    const map = this.mapping[index];

    this.mapping.splice(index, 1);
    this.templateKeys.push(map.destination);
  }

  closeDialog(event: Event): void {
    event.stopPropagation();

    this.dialogRef.close();
  }

  submit(event: Event): void {
    event.stopPropagation();

    this.formAutocomplete.updateValueAndValidity();
    this.templateAutocomplete.updateValueAndValidity();
    this.categoryAutocomplete.updateValueAndValidity();

    if (
      this.templateKeys.length ||
      this.formAutocomplete.isInvalid() ||
      this.templateAutocomplete.isInvalid() ||
      this.categoryAutocomplete.isInvalid()
    ) {
      this.mapped = false;
      return;
    }

    this.mapped = true;

    const formTemplateMap: FieldTemplateMap = {
      id: this.link.id,
      model_type: 'FORM',
      model: this.formAutocomplete.value,
      model_version:
        this.link.require_reset || !this.link.id
          ? this.formAutocomplete.value?.version
          : this.link.model_version,
      template: this.templateAutocomplete.value,
      document_category_id: this.categoryAutocomplete.value?.id,
      mapping: JSON.stringify(this.mapping),
    };

    this.link.id
      ? this.store.dispatch(new UpdateFormTemplateMapOnServer({ formTemplateMap }))
      : this.store.dispatch(new CreateFormTemplateMapOnServer({ formTemplateMap }));

    this.dialogRef.close(true);
  }
}
